import styles from './pageOrientation.module.scss';
import LiteralsService from 'src/services/literals/LiteralsService';
import FormatService from 'src/services/format.service';
import { LocalDate } from '@js-joda/core';
import DateUtils from 'src/utils/date';
import React from 'react';
import { trackClick } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import { useHistory, useLocation } from 'react-router-dom';
import H from 'history';
import Selectors from 'src/utils/selectors';
import { ButtonPrimary, GlyphRight } from '@americanexpress/dls-react';
import { PageTimePeriod } from 'src/types/page-time-period.enum';
import Output from '../output/Output';
import { BUDGET_OVERALL_ID } from 'src/services/budget.service';
import { useSpendingHistory, useSpendingSumForCategory } from 'src/hooks/queries/spending';
import ExpandableText from 'src/components/atomic/molecules/ExpandableText/ExpandableText';
import CategoryEditButton from 'src/components/atomic/atoms/categoryEditButton/CategoryEditButton';
import useCurrentUser from 'src/hooks/useCurrentUser';
import { DLSSize } from 'src/types/dls-size.enum';
import { goToSettings } from 'src/services/router.service';

export interface PageOrientationProps {
    timePeriod: PageTimePeriod;
    propHistory?: H.History;
}

export default function PageOrientation({ timePeriod, propHistory }: PageOrientationProps): JSX.Element {
    const { isFullPlanUser } = useCurrentUser();
    const history = useHistory();
    const location = useLocation();
    const isIndexPage = location.pathname.includes('/home');
    const isCurrentMonth: boolean = timePeriod === PageTimePeriod.CURRENT_MONTH;
    const isLastYear: boolean = timePeriod === PageTimePeriod.LAST_YEAR;

    const { isLoading: isLoadingSpendingSums, data: spendingSums } = useSpendingSumForCategory({
        id: BUDGET_OVERALL_ID,
        startDate: DateUtils.getDatesFromTimePeriod(PageTimePeriod.CURRENT_MONTH).startDate,
        endDate: DateUtils.getDatesFromTimePeriod(PageTimePeriod.CURRENT_MONTH).endDate,
        enabled: isCurrentMonth
    });

    const { isLoading: isLoadingSpendingHistory, data: spendingHistory } = useSpendingHistory({
        id: BUDGET_OVERALL_ID,
        startDate: LocalDate.now().minusYears(1).withDayOfMonth(1),
        endDate: LocalDate.now().withDayOfMonth(1),
        enabled: isLastYear
    });

    const actionButtons: JSX.Element = (
        <div className={styles.actionButtons}>
            <ButtonPrimary
                onClick={() => {
                    trackClick(location.pathname, adobeTags.CurrentMonth.AddAccount);
                    goToSettings(isFullPlanUser, propHistory || history);
                }}
                role="link"
                data-cy={Selectors.SpendSmart.ADD_EXTERNAL_ACCOUNT_BUTTON}
            >
                {LiteralsService.getLiteral('PageOrientation.linkAndManageAccounts')}
                <GlyphRight size={DLSSize.SMALL} className={styles.glyphRight} />
            </ButtonPrimary>
            <CategoryEditButton location="top" />
        </div>
    );

    return (
        <div className={styles.pageOrientation}>
            <div className={styles.output}>
                <Output
                    title={
                        isCurrentMonth
                            ? LiteralsService.getLiteral('Budget.Current.currentSpendingThisMonth')
                            : LiteralsService.getLiteral('Budget.History.averageMonthlySpending')
                    }
                    titleComponent="h2"
                    main={FormatService.formatCurrency(
                        isCurrentMonth ? spendingSums?.spent : spendingHistory?.[0]?.averageSpent,
                        { round: !isIndexPage }
                    )}
                    mainIsLoading={isCurrentMonth ? isLoadingSpendingSums : isLoadingSpendingHistory}
                />
                {isLastYear && (
                    <ExpandableText
                        title={LiteralsService.getLiteral(`Budget.History.collapsibleSection.title`)}
                        body={LiteralsService.getLiteral(`Budget.History.collapsibleSection.body`)}
                        className={isCurrentMonth ? '' : styles.historyCollapsible}
                        tagName={
                            isCurrentMonth ? adobeTags.CurrentMonth.ExpandableText : adobeTags.History.ExpandableText
                        }
                    />
                )}
            </div>
            {isCurrentMonth && actionButtons}
        </div>
    );
}
